import { useEffect } from "react";
import { Outlet, createFileRoute, useMatch } from "@tanstack/react-router";
import { Page } from "@sx/ui";

import {
  registerWorkspaceHeader,
  type ProjectAddress,
  type WorkspaceNavItem,
} from "../../../lib/nav";
import { useProjectAddress, useProjectId } from "../../../shell/project";
import { TrainingProjectProvider } from "../../../workspaces/training/project";

export const Route = createFileRoute("/_console/p/$org/$project/training")({
  component: TrainingLayout,
});

const trainingNav = (
  params: ProjectAddress,
): readonly WorkspaceNavItem[] => [
  { to: "/p/$org/$project/training", params, label: "Training gym", icon: "experiment", end: true },
  { to: "/p/$org/$project/training/runs", params, label: "Runs", icon: "lineage" },
  { to: "/p/$org/$project/training/compare", params, label: "Compare", icon: "compare" },
  {
    to: "/p/$org/$project/training/research",
    params,
    label: "Auto research",
    icon: "auto",
  },
];

/* Training composes three project-scoped owners: Autonomy jobs, Catalog corpora,
   and Waddle evidence. No one of those grants stands in for the other two, so
   each page asks the door it actually uses and renders that door's refusal.

   Training uses the shared command-page geometry because launch specifications,
   job tables and comparisons benefit from the wider operator canvas. A run is an
   instrument: its facts column and chart wall take the whole width, edge to edge. */
function TrainingLayout() {
  const projectId = useProjectId();
  const address = useProjectAddress();
  const runOpen =
    useMatch({
      from: "/_console/p/$org/$project/training/runs/$runId",
      shouldThrow: false,
    }) !== undefined;

  useEffect(() => {
    return registerWorkspaceHeader({
      workspace: "autonomy-training",
      items: trainingNav(address),
      badges: [],
    });
  }, [address]);

  return (
    <TrainingProjectProvider projectId={projectId}>
      {runOpen ? (
        <Page layout="workbench">
          <Outlet />
        </Page>
      ) : (
        <Page layout="page" spacing="compact">
          <Outlet />
        </Page>
      )}
    </TrainingProjectProvider>
  );
}
